/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from "axios";
import store from "store";
import { Timestamp, doc, updateDoc, deleteField, getDoc } from "firebase/firestore";
import { ApiServerPath } from "../api_server";
import { addPlatformService, storeProfileImage, sweetAlert } from "./global";
import {
  PLATFORM_SERVICES_COLLECTION,
  USER_DATA_AND_CHATBOT_COLLECTION,
} from "constants/global/firebase-collections";
import {
  getAccountLocks,
  getSocialLockRejectionMessage,
  isSocialIdentityAllowed,
} from "./accountLocks";

// Token exchange - returns token data only
export const getTikTokSocialAccessToken = async (authCode: any) => {
  try {
    const { userData = {} }: any = store.getState().appReducer;
    const { lastSelectedWorkspace = {} } = userData;
    const { workspaceId } = lastSelectedWorkspace;

    const redirectUri =
      process.env.NODE_ENV === "development"
        ? "http://localhost:3000/"
        : "https://app.advertgenerator.com/";

    // Exchange code for tokens
    const { data } = await axios.post(
      `${ApiServerPath.API_SERVER_BASE_URL}${ApiServerPath.GET_TIKTOK_SOCIAL_ACCESS_TOKEN}`,
      { code: authCode, redirectUri },
    );

    if (data?.success && data?.results) {
      const {
        accessToken,
        refreshToken,
        openId,
        expiresIn,
        refreshExpiresIn,
        scope,
      } = data.results;

      // Enforce permanent TikTok social lock when present.
      // Lock *check* failures stay non-blocking; a rejected identity must
      // always abort — even if cleanup updateDoc fails.
      try {
        const userDataSnap = await getDoc(
          doc(USER_DATA_AND_CHATBOT_COLLECTION, workspaceId),
        );
        const locks = getAccountLocks(userDataSnap.data() || {});
        if (!isSocialIdentityAllowed(locks, "tiktok", { openId })) {
          sweetAlert(getSocialLockRejectionMessage(locks, "tiktok"), "error");
          try {
            const ref = doc(PLATFORM_SERVICES_COLLECTION, workspaceId);
            await updateDoc(ref, { tiktokSocial: deleteField() });
          } catch (cleanupError) {
            console.log("TikTok social lock cleanup failed:", cleanupError);
          }
          return { success: false, locked: true };
        }
      } catch (lockError) {
        console.log("TikTok social lock check failed (non-blocking):", lockError);
      }

      // Save base tokens using standard addPlatformService
      await addPlatformService(
        "tiktokSocial",
        refreshToken,
        workspaceId,
        accessToken,
      );

      // Fetch user info (wait for it to complete)
      const userInfo = await getTikTokSocialUserInfo(accessToken);

      // Store profile image permanently in Firebase Storage
      if (userInfo?.userImage) {
        const profileImageUrl = await storeProfileImage(
          userInfo.userImage,
          "tiktok",
        );
        // Only update if storage succeeded (returns URL string, null if failed)
        if (profileImageUrl) {
          userInfo.profileImageUrl = profileImageUrl;
        }
      }

      // BATCH WRITE: Combine all TikTok fields into single Firestore update
      const ref = doc(PLATFORM_SERVICES_COLLECTION, workspaceId);
      await updateDoc(ref, {
        "tiktokSocial.accessToken": accessToken,
        "tiktokSocial.openId": openId,
        "tiktokSocial.expiresIn": expiresIn,
        "tiktokSocial.refreshExpiresIn": refreshExpiresIn,
        "tiktokSocial.scope": scope,
        "tiktokSocial.createdAt": Timestamp.now(),
        "tiktokSocial.displayName": userInfo?.displayName || null,
        "tiktokSocial.userImage": userInfo?.userImage || null,
        "tiktokSocial.userName": userInfo?.displayName || null,
        "tiktokSocial.profileImageUrl": userInfo?.profileImageUrl || null,
      });

      return data;
    }

    return data;
  } catch (error) {
    console.error("Error exchanging TikTok Social token:", error);
    throw error;
  }
};

// Separate endpoint for user profile (returns data, doesn't write to Firestore)
export const getTikTokSocialUserInfo = async (accessToken: string) => {
  try {
    const { data } = await axios.post(
      `${ApiServerPath.API_SERVER_BASE_URL}${ApiServerPath.GET_TIKTOK_SOCIAL_USER_INFO}`,
      { accessToken },
    );

    if (data?.success && data?.results) {
      return data.results;
    }

    return null;
  } catch (error) {
    console.error("Error fetching TikTok Social user info:", error);
    // Don't throw - user info failure shouldn't fail the entire flow
    return null;
  }
};
